'use client';

/**
 * useChallengeMotion.ts
 *
 * Samples DeviceMotion (gyro rotationRate + high-passed accel) while a
 * server-issued face challenge is executing, and produces the motion summary
 * that rides inside FaceChallengeEvidence.motion.
 *
 * Why: a replayed video can satisfy a visual challenge on screen, but the
 * artifact can't make the DEVICE rotate — a hand-held phone always shows
 * micro-tremor (~1-5°/s RMS). The summary is corroborating evidence, not a
 * hard gate: mounted kiosks legitimately score flat, so the server flags
 * rather than rejects on it.
 *
 * iOS 13+ requires DeviceMotionEvent.requestPermission() from a user gesture.
 * We attempt it lazily on first sampling; denial just means "unreported" —
 * never blocks the challenge.
 */

import { useCallback, useEffect, useRef } from 'react';

export interface ChallengeMotionSummary {
  peak_deg_s: number;
  rms_deg_s: number;
  samples: number;
}

interface UseChallengeMotionResult {
  /** Begin sampling. Safe to call repeatedly — resets accumulators and
   *  attaches the listener once. */
  start: () => void;
  /** Stop sampling and return the summary (or null when nothing captured). */
  stop: () => ChallengeMotionSummary | null;
}

export function useChallengeMotion(): UseChallengeMotionResult {
  const attachedRef = useRef(false);
  const sumSqRef = useRef(0);
  const peakRef = useRef(0);
  const countRef = useRef(0);
  const accelEmaRef = useRef<{ x: number; y: number; z: number } | null>(null);
  const handlerRef = useRef<((e: DeviceMotionEvent) => void) | undefined>(undefined);

  const handleMotion = useCallback((event: DeviceMotionEvent) => {
    const rot = event.rotationRate;
    const acc = event.acceleration ?? event.accelerationIncludingGravity;

    const rotMag = rot
      ? Math.sqrt((rot.alpha || 0) ** 2 + (rot.beta || 0) ** 2 + (rot.gamma || 0) ** 2)
      : 0;

    // High-pass accelerometer (EMA removes gravity) for linear motion.
    let linMag = 0;
    if (acc && acc.x != null && acc.y != null && acc.z != null) {
      const ema = accelEmaRef.current || { x: acc.x, y: acc.y, z: acc.z };
      linMag = Math.sqrt((acc.x - ema.x) ** 2 + (acc.y - ema.y) ** 2 + (acc.z - ema.z) ** 2);
      accelEmaRef.current = {
        x: ema.x + 0.15 * (acc.x - ema.x),
        y: ema.y + 0.15 * (acc.y - ema.y),
        z: ema.z + 0.15 * (acc.z - ema.z),
      };
    }

    const magnitude = Math.max(rotMag, linMag * 10); // accel ~m/s² scaled into deg/s-ish magnitude for a single summary
    sumSqRef.current += magnitude * magnitude;
    if (magnitude > peakRef.current) peakRef.current = magnitude;
    countRef.current += 1;
  }, []);

  const requestIOSPermission = useCallback(async () => {
    try {
      const DME = (window as { DeviceMotionEvent?: unknown }).DeviceMotionEvent as
        | { requestPermission?: () => Promise<string> }
        | undefined;
      if (typeof DME?.requestPermission === 'function') {
        await DME.requestPermission();
      }
    } catch {
      // Denial/no-gesture → sampling stays silent; motion reports "unreported".
    }
  }, []);

  const start = useCallback(() => {
    if (typeof window === 'undefined' || !('DeviceMotionEvent' in window)) return;
    sumSqRef.current = 0;
    peakRef.current = 0;
    countRef.current = 0;
    accelEmaRef.current = null;
    if (!attachedRef.current) {
      handlerRef.current = handleMotion;
      window.addEventListener('devicemotion', handleMotion);
      attachedRef.current = true;
      void requestIOSPermission();
    }
  }, [handleMotion, requestIOSPermission]);

  const stop = useCallback((): ChallengeMotionSummary | null => {
    if (attachedRef.current && handlerRef.current) {
      window.removeEventListener('devicemotion', handlerRef.current);
      attachedRef.current = false;
    }
    if (countRef.current === 0) return null;
    return {
      peak_deg_s: Number(peakRef.current.toFixed(2)),
      rms_deg_s: Number(Math.sqrt(sumSqRef.current / countRef.current).toFixed(2)),
      samples: countRef.current,
    };
  }, []);

  // Never leave the sensor listener attached past the component's life.
  useEffect(() => () => {
    if (attachedRef.current && handlerRef.current) {
      window.removeEventListener('devicemotion', handlerRef.current);
      attachedRef.current = false;
    }
  }, []);

  return { start, stop };
}
